import { ChevronRight, ChevronLeft } from "lucide-react";
import { AccuracyIcon, CardIconInspectionSearch } from "../../components/svg";
import { Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useEffect, useState } from "react";

const ScrollableSplitCardBlock = ({
  site,
  cardContainerRef,
  scrollLeft,
  scrollRight,
  showLeftButton,
  showRightButton,
  cardWidth,
  title,
  isSplitScreen,
  accuracyData
}: any) => {
  const [siteWithAccuracy, setSiteWithAccuracy] = useState<any>({});
  useEffect(() => {
    if (!site) return;

    let cardsData = [...(site.cardsData || [])];
    let accuracyValue = "0%";

    if (accuracyData?.length > 0) {
      const matched = accuracyData.find(
        (acc: any) => acc.subProcessUniqueId === site.SubprocessId
      );

      if (matched?.accuracy) {
        const num = parseFloat(matched.accuracy.replace("%", ""));
        accuracyValue = `${num.toFixed(2)}%`;
      }

      const lastCard = {
        uniqueId: "accuracy",
        status: "Current accuracy",
        value: accuracyValue,
        backgroundColor: "#d9d2e9",
        fontColor: "#ffee32",
        imageUrl: "",
      };

      cardsData = [...cardsData, lastCard];
    }

    setSiteWithAccuracy({
      ...site,
      accuracy: accuracyValue,
      cardsData,
    });
  }, [site, accuracyData]);

  const getIconForStatus = (status: string) => {
    const safeStatus = status?.toLowerCase();

    switch (safeStatus) {
      case "sampled":
        return <CardIconInspectionSearch />;
        case "current accuracy":
          return <AccuracyIcon/>
      default:
        return <CardIconInspectionSearch />;
    }
  };

  const allCards = [ ...(siteWithAccuracy?.cardsData || []), ];

  return (
    <div className="relative w-full mb-18 h-full">
      <h1 className="text-[2.5rem] text-io-black font-inter font-normal dark:text-io-white ml-[1rem]">
        {title}
      </h1>
      {showLeftButton && (
        <button
          onClick={scrollLeft}
          className="absolute left-0 top-1/2 transform -translate-y-1/2 bg-gray-300 p-2 rounded-full shadow-lg z-10"
        >
          <ChevronLeft size={24} />
        </button>
      )}

      <div
        className="overflow-x-auto scrollbar-hide px-2"
        ref={cardContainerRef}
        style={{
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
      >
        <div className="flex flex-col gap-6">
          <div className="flex flex-nowrap gap-4 justify-start w-full">
            {allCards.map((card: any, index: number) => {
              const totalCards = allCards.length;
              const maxWidthPercent = 100 / totalCards - 0.7;

              return (
                <div
                  key={card.uniqueId + index}
                  className={`rounded-2xl shadow-md mb-4 p-8 ${
                    index === 0
                      ? "bg-[#0078D4] text-white"
                      : "bg-[#F9FAFB] text-black"
                  } h-full`}
                  style={{
                    minWidth: cardWidth,
                    height: "30vh",
                    flexBasis: `${100 / allCards.length - 0.7}%`,
                    maxWidth: `${100 / allCards.length - 0.7}%`,
                  }}
                >
                  <div className="flex justify-between items-start h-full">
                    {/* Text Section */}
                    <div className="flex flex-col justify-between h-full">
                      <p
                        className={"font-normal font-inter text-[2rem] md:text-[3rem]"}
                      >
                        {card.status}
                      </p>
                      <p className="font-semibold font-[Albert Sans] capitalize pt-2 text-[2rem] md:text-[3rem] lg:text-[4rem]">
                        {card.value}
                      </p>
                    </div>

                    {/* Icon Section - stays round & responsive */}
                    <div
                      className="flex-shrink-0 rounded-full flex items-center justify-center"
                      style={{
                        width: 64,
                        height: 64,
                        backgroundColor:
                          card.status.toLowerCase() === "captured"
                            ? "rgba(255, 255, 255, 0.2)"
                            : card.fontColor,
                      }}
                    >
                      {["sampled", "captured", "current accuracy"].includes(
                        card.status.toLowerCase()
                      ) ? (
                        <div className="object-contain w-3/4 h-3/4 p-4">
                          {getIconForStatus(card.status)}
                        </div>
                      ) : (
                        <img
                          src={card.imageUrl}
                          alt={card.status}
                          className="object-contain w-3/4 h-3/4 p-4"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src =
                              "/icons/default.png";
                          }}
                        />
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {showRightButton && (
        <button
          onClick={scrollRight}
          className="absolute right-0 top-1/2 transform -translate-y-1/2 bg-gray-300 p-2 rounded-full shadow-lg z-10"
        >
          <ChevronRight size={24} />
        </button>
      )}
    </div>
  );
};

export default ScrollableSplitCardBlock;
